import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { getClassName } from 'utils/ui/classes';
import ChevronLeftIcon from './icons/ChevronLeftIcon';
import ChevronRightIcon from './icons/ChevronRightIcon';

const containerClass =
  'flex justify-between px-4 py-3 bg-white border-t border-gray-200 sm:px-6';
const paginationClass =
  'relative inline-flex items-center px-2 py-2 text-sm font-medium border border-gray-300';

const Pagination = ({
  page,
  pages,
  perPage,
  total,
  showCount,
  items,
  onPageClick,
  pageItemsPerPage,
  className,
  paginationContainerClassName,
  dataTestId,
}) => {
  const { t } = useTranslation();
  const isLastPage = page === pages;

  let end;
  const maxPage = Math.ceil(page / pageItemsPerPage) * pageItemsPerPage;
  const previousPageItemsMax = maxPage - pageItemsPerPage;
  const lastPage = maxPage < pages ? maxPage : pages;
  const firstPage = previousPageItemsMax + 1;
  const pageItemsCount = lastPage - previousPageItemsMax;

  const previousPageLastRecord = (page - 1) * perPage;
  const start = previousPageLastRecord + 1;

  const isPrevDisabled = previousPageItemsMax === 0;
  const isNextDisabled = maxPage >= pages;
  if (isLastPage) {
    end = total || previousPageLastRecord + items.length;
  } else {
    end = previousPageLastRecord + perPage;
  }

  function handlePrevClick() {
    if (!isPrevDisabled) {
      onPageClick(previousPageItemsMax - pageItemsPerPage + 1);
    }
  }

  function handleNextClick() {
    if (!isNextDisabled) {
      onPageClick(maxPage + 1);
    }
  }

  function handlePageClick(e) {
    const {
      target: { value: targetPage },
    } = e;
    e.preventDefault();
    onPageClick(parseInt(targetPage, 10));
  }

  const pageNumbers = Array.from(Array(pageItemsCount).keys()).map(
    (i) => i + firstPage,
  );

  return (
    <div
      data-testid={dataTestId}
      className={getClassName(containerClass, className)}>
      {total === 0 && (
        <div
          data-testid="div-58eec07f"
          className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
          &nbsp;
        </div>
      )}
      {showCount && total > 0 && (
        <div
          data-testid="paginationInfo"
          className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between"
        >
          <p data-testid="p-1fab6ec5" className="text-xm text-gray-700">
            {t('PAGINATION_RESULTS', { start: page, total })}
          </p>
        </div>
      )}
      <div
        data-testid="paginationControls"
        className={`${paginationContainerClassName} flex items-center`}
      >
        <button
          data-testid="prev-btn"
          className="relative inline-flex items-center px-2 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-l-md hover:bg-gray-50"
          disabled={isPrevDisabled}
          onClick={handlePrevClick}
        >
          <span data-testid="span-100cec53" className="sr-only">{t('PREVIOUS')}</span>
          <ChevronLeftIcon className="w-5 h-5" />
        </button>
        <nav
          data-testid="nav-5528a6fa"
          className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
          aria-label="Pagination">
          {pageNumbers.map((currentPage) => {
            const selected = currentPage === page;
            return (
              <button
                data-testid={
                  !selected ? 'page-btn-' + currentPage : 'current-page'
                }
                key={currentPage}
                value={currentPage}
                onClick={handlePageClick}
                className={getClassName(paginationClass, {
                  'text-gray-700': !selected,
                  'bg-white': !selected,
                  'text-white': selected,
                  'bg-green-cm': selected,
                  'hover:bg-gray-50': !selected,
                })}
              >
                {currentPage}
              </button>
            );
          })}
        </nav>
        <button
          data-testid="next-btn"
          className="relative inline-flex items-center px-2 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-r-md hover:bg-gray-50"
          disabled={isNextDisabled}
          onClick={handleNextClick}
        >
          <span data-testid="span-1ebb5ac2" className="sr-only">{t('NEXT')}</span>
          <ChevronRightIcon className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
};

Pagination.propTypes = {
  page: PropTypes.number,
  pages: PropTypes.number.isRequired,
  perPage: PropTypes.number,
  total: PropTypes.number,
  showCount: PropTypes.bool,
  items: PropTypes.array,
  onPageClick: PropTypes.func.isRequired,
  pageItemsPerPage: PropTypes.number,
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.array,
    PropTypes.object,
  ]),
  paginationContainerClassName: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.array,
    PropTypes.object,
  ]),
};

Pagination.defaultProps = {
  page: 1,
  perPage: 20,
  pageItemsPerPage: 10,
  total: 0,
  pages:0,
  showCount: true,
  items: [],
  paginationContainerClassName: '',
};

export default Pagination;
